import { TOPICS, type Topic } from './topics';

/**
 * Understanding a child's short spoken answer from a speech-to-text transcript, without a
 * language model: spoken choices ("number two", "the second one", "yes", "up") by matching words
 * that sound alike, and questions by keyword overlap with the approved topics (the fallback when
 * the language model is missing or unsure).
 */

/** Lowercase words, punctuation removed. */
export function words(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9' ]+/g, ' ')
    .split(/\s+/)
    .filter(Boolean);
}

/** Words speech-to-text writes for each spoken choice word (homophones and near-misses). */
const SOUNDS_LIKE: Readonly<Record<string, readonly string[]>> = {
  one: ['1', 'won', 'first', 'wan', 'juan'],
  two: ['2', 'to', 'too', 'second', 'tu'],
  three: ['3', 'third', 'tree', 'free'],
  yes: ['yeah', 'yep', 'yup', 'sure', 'ok', 'okay', 'uh-huh', 'yes please', 'ya'],
  no: ['nope', 'nah', 'no thanks'],
  up: ['top', 'high', 'higher', 'raise', 'raised'],
  down: ['low', 'lower', 'bottom', 'downward'],
  again: ['repeat', 'replay', 'more', 'another'],
  next: ['continue', 'go on', 'done', 'ready', 'go'],
  stop: ['break', 'pause', 'enough', 'quit', 'tired'],
};

const ORDINALS = new Set([
  'first',
  'second',
  'third',
  'last',
  'other',
  'that',
  'this',
  'blue',
  'pink',
]);

/** A choice the child can say: its id and the words that pick it. */
export interface Choice {
  id: string;
  /** Canonical spoken words (each expanded with SOUNDS_LIKE). */
  says: readonly string[];
}

function expand(say: string): string[] {
  return [say, ...(SOUNDS_LIKE[say] ?? [])];
}

function containsPhrase(ws: readonly string[], phrase: string): boolean {
  const p = words(phrase);
  for (let i = 0; i + p.length <= ws.length; i++)
    if (p.every((w, j) => ws[i + j] === w)) return true;
  return false;
}

/** Words before "one" that make it a pronoun ("the fun one", "that one"), not the number. */
const ARTICLES = new Set(['the', 'a', 'that', 'this', 'other', 'which', 'any', 'each', 'another']);
/** Self-corrections: what follows the last of these is the answer. */
const CORRECTIONS = [
  ['no', 'wait'],
  ['wait', 'no'],
  ['i', 'mean'],
  ['actually'],
  ['scratch', 'that'],
  ['or', 'not'],
];
/** Words that negate what follows them ("I don't wanna kiss"). */
const NEGATIONS = new Set([
  'not',
  'dont',
  "don't",
  'never',
  'isnt',
  "isn't",
  'doesnt',
  "doesn't",
  'aint',
  "ain't",
]);
/** Question words that open a question rather than an answer ("what's next?"). */
const WH = new Set(['what', "what's", 'whats', 'why', 'how', 'where', 'when', 'who']);

/** Where each occurrence of a phrase starts in the words. */
function occurrences(ws: readonly string[], phrase: string): number[] {
  const p = words(phrase);
  const at: number[] = [];
  for (let i = 0; i + p.length <= ws.length; i++)
    if (p.every((w, j) => ws[i + j] === w)) at.push(i);
  return at;
}

/** The words after the last self-correction (null: corrected to nothing, as in "up... or not"). */
function afterCorrection(ws: readonly string[]): readonly string[] | null {
  let cut = -1;
  for (const marker of CORRECTIONS)
    for (const i of occurrences(ws, marker.join(' '))) cut = Math.max(cut, i + marker.length);
  if (cut < 0) return ws;
  const rest = ws.slice(cut);
  return rest.length ? rest : null;
}

/**
 * The choice the transcript picks, or null when none or more than one is named ("one or two"
 * stays undecided, so the helper asks again instead of guessing). Read the way children answer:
 * a self-correction counts from its last part ("down, no wait, up"), a negated option is no
 * answer ("I don't wanna kiss"), "the fun one" is not "one", and "what's next?" is a question.
 */
export function matchChoice(text: string, choices: readonly Choice[]): string | null {
  const all = words(text);
  if (WH.has(all[0] ?? '')) return null;
  const said = afterCorrection(all);
  if (!said) return null;
  // "the second one", "the fun one": the "one" is a pronoun, not the number.
  const ws = said.filter(
    (w, i, a) =>
      !(
        w === 'one' &&
        (ORDINALS.has(a[i - 1] ?? '') ||
          ARTICLES.has(a[i - 1] ?? '') ||
          ARTICLES.has(a[i - 2] ?? ''))
      ),
  );
  const where = choices.map((c) => ({
    id: c.id,
    at: c.says.flatMap((s) => expand(s).flatMap((x) => occurrences(ws, x))),
  }));
  // A negation just before an option ("not the back", "don't wanna kiss"): no answer.
  const negated = where.some(({ at }) =>
    at.some((i) => ws.slice(Math.max(0, i - 3), i).some((w) => NEGATIONS.has(w))),
  );
  if (negated) return null;
  const hits = where.filter(({ at }) => at.length > 0);
  return hits.length === 1 ? (hits[0]?.id ?? null) : null;
}

/** Question words: an utterance with one is a question even without a question mark. */
const QUESTION = [
  'what',
  'why',
  'how',
  'where',
  'when',
  'which',
  'who',
  'can',
  'do',
  'does',
  'is',
  'should',
];

/** Whether the transcript reads like a question to the helper. */
export function isQuestion(text: string): boolean {
  const ws = words(text);
  return text.includes('?') || (ws.length >= 3 && QUESTION.includes(ws[0] ?? ''));
}

/**
 * The approved topic whose keys the transcript shares most (whole words or phrases), with the
 * number of keys matched; null when none matches.
 */
export function keywordTopic(
  text: string,
  topics: readonly Topic[] = TOPICS,
): { topic: Topic; keys: number } | null {
  const ws = words(text);
  let best: { topic: Topic; keys: number } | null = null;
  for (const topic of topics) {
    const keys = topic.keys.filter((k) => containsPhrase(ws, k)).length;
    if (keys > 0 && (!best || keys > best.keys)) best = { topic, keys };
  }
  return best;
}
